import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { FEATURE_FLAGS, LOCALSTORAGE_KEY_IMPERSONATOR_ID } from '~/constants';
import { LocalStorageService } from '~/core/utils/storage';
import { useGetUserSettingsById } from '~/hooks/api/user/useGetUserSettingsById';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';

import {
  FLUID_FOAM_URL,
  isCrossEnvironmentHandOff,
  resolveFluidPath,
} from './fluidRoutes';

/**
 * Sends a user who has opted into fluid to the fluid equivalent of the route
 * they are on.
 *
 * Mounted once, app-wide. It used to live inside `TryNewFoamBanner`, which only
 * renders under `LoggedLayout` — so pages with their own layout never bounced at
 * all. `/media-kits/[id]/builder` uses `CustomShareablesLayout` and `/` uses no
 * layout, so both stranded fluid users in classic (FOAM-22396). Whether a route
 * hands off is a property of the route, not of the chrome around it.
 *
 * Safe to run everywhere:
 * - Routes with no fluid counterpart resolve to `null` and stay put.
 * - Public share routes are absent from the map, so shared links never hand off.
 * - Anonymous visitors have no manager record, so the settings query never runs
 *   and `settings.data` stays undefined.
 * - Impersonating admins never hand off (FOAM-22543): the JWT is the subject's,
 *   so `interfaceType` here is the *impersonated* user's preference, and the
 *   impersonation session does not carry across to the fluid origin.
 */
export const useFluidInterfaceRedirect = () => {
  const router = useRouter();
  const { isEnabled } = useFeature(FEATURE_FLAGS.foamTryFluid);
  const settings = useGetUserSettingsById();

  useEffect(() => {
    // No configured fluid origin means the hand-off is switched off — the local
    // kill switch, and a guard against redirecting to `https://undefined` in an
    // environment that never set the var.
    if (!FLUID_FOAM_URL) return;
    // Never eject a developer off their own machine to a deployed fluid.
    if (isCrossEnvironmentHandOff(window.location.hostname, FLUID_FOAM_URL)) {
      return;
    }
    if (!isEnabled) return;
    // An impersonating admin stays in the app they impersonated in. Reading
    // inside the effect is safe: impersonation start/stop always goes through
    // a full page reload, so the key can't change under a mounted hook.
    if (LocalStorageService.getItem(LOCALSTORAGE_KEY_IMPERSONATOR_ID)) return;
    if (settings.data?.interfaceType !== 'fluid') return;

    const fluidPath = resolveFluidPath(router.pathname, router.query);
    if (!fluidPath) return;

    window.location.assign(`${FLUID_FOAM_URL}${fluidPath}`);
  }, [isEnabled, settings.data, router.pathname, router.query]);
};
